// utils/apiError.js
//
// Normalises anything that can go wrong into one shape, so call sites do not
// each have to work out whether they are holding a thrown TypeError, a failed
// Response, or an API body that said success:false.
//
// That inconsistency is the root of the current problem: the portal has 1,567
// error paths and no agreement on what an "error" is, so a network blip and a
// permission denial both end up as `err.message` in a toast.

import {
    userMessageFor, categorize, shouldReport, isSessionEnded, ERROR_CODES,
} from '@/utils/errorMessages';

/**
 * @typedef {object} NormalisedError
 * @property {string}  category      Transport | Application | Client | Unknown
 * @property {string}  userMessage   safe to show; never technical
 * @property {string}  [rawMessage]  server/browser text — for the log only
 * @property {number}  [errorCode]   LoginErrorCodes
 * @property {number}  [httpStatus]
 * @property {Error}   [error]
 * @property {boolean} sessionEnded  the user must sign in again
 * @property {boolean} report        should go to the error log
 */

function currentlyOnline() {
    if (typeof navigator === 'undefined') return undefined;
    return navigator.onLine;
}

/**
 * Build a normalised error from a thrown exception.
 * Use in a `catch`.
 */
export function fromException(error, context = {}) {
    const online = currentlyOnline();
    const base = { error, online, rawMessage: error?.message };
    const category = categorize(base);
    const errorCode = undefined;
    return {
        category,
        userMessage: userMessageFor(base),
        rawMessage: error?.message,
        errorCode,
        httpStatus: undefined,
        error,
        sessionEnded: false,
        report: shouldReport({ category, errorCode }),
        ...context,
    };
}

/**
 * Build a normalised error from an API body that came back `success: false`.
 * Expects the camelCased shape produced by `normalizeKeys`.
 */
export function fromApiResponse(data = {}, context = {}) {
    const online = currentlyOnline();
    const errorCode = typeof data.errorCode === 'number' ? data.errorCode : undefined;
    const rawMessage = data.errorMessage || undefined;
    const base = { errorCode, rawMessage, online };
    const category = categorize(base);
    return {
        category,
        userMessage: userMessageFor(base),
        rawMessage,
        errorCode,
        httpStatus: undefined,
        sessionEnded: isSessionEnded({ errorCode, rawMessage }),
        report: shouldReport({ category, errorCode }),
        ...context,
    };
}

/**
 * Build a normalised error from a non-OK HTTP Response.
 *
 * Reads the body defensively: a gateway can answer with an HTML error page, and
 * that whole page has previously ended up in a toast. Anything that looks like
 * markup, or is implausibly long, is discarded in favour of the status.
 */
export async function fromHttpResponse(res, context = {}) {
    const httpStatus = res?.status;
    let rawMessage;

    try {
        const text = (await res.text())?.trim() || '';
        const looksLikeHtml = /^<!doctype|^<html|<\/html>/i.test(text);
        if (text && !looksLikeHtml && text.length <= 300) {
            rawMessage = text;
        } else if (looksLikeHtml) {
            rawMessage = `HTML error page (${httpStatus})`;
        }
    } catch {
        // Body already consumed or unreadable — the status alone is enough.
    }

    const online = currentlyOnline();
    const base = { httpStatus, rawMessage, online };
    const category = categorize(base);
    return {
        category,
        userMessage: userMessageFor(base),
        rawMessage,
        errorCode: undefined,
        httpStatus,
        sessionEnded: httpStatus === 401,
        report: shouldReport({ category, errorCode: undefined }) || httpStatus >= 500,
        ...context,
    };
}

/**
 * Catch-all when you are not sure what you are holding.
 * Handy while migrating existing call sites.
 */
export function fromAnything(thing, context = {}) {
    // Already normalised — pass it through, merging any extra context.
    //
    // This case is NOT rare: every call site written as
    //   handleError(await fromHttpResponse(res), { action })
    // lands here. Without this branch the object fails the `instanceof Error`
    // and `errorCode in thing` checks below, falls through to
    // `new Error(String(thing))` → "[object Object]", and every carefully
    // derived field — category, userMessage, status — is replaced by a generic
    // "Something went wrong". Caught by a live test, not by the unit tests,
    // which had only ever passed raw inputs in.
    // `thing &&` first: typeof null === 'object', so the `in` checks below throw
    // on null without it.
    if (thing && typeof thing === 'object' && 'category' in thing && 'userMessage' in thing) {
        return { ...thing, ...context };
    }

    if (!thing) {
        return {
            category: 'Unknown',
            userMessage: userMessageFor({}),
            sessionEnded: false,
            report: true,
            ...context,
        };
    }
    if (thing instanceof Error) return fromException(thing, context);
    if (typeof thing === 'object' && ('errorCode' in thing || 'errorMessage' in thing)) {
        return fromApiResponse(thing, context);
    }
    return fromException(new Error(String(thing)), context);
}

export { ERROR_CODES };
